/**
 * The tenant switcher — org mark in the top bar, dropdown of every organization
 * the user can reach.
 *
 * Why it lives in the shell rather than in each app: switching organization was a
 * per-app *page* (Optimize's /dashboard, and a picker in every other app), so
 * changing tenant meant leaving what you were doing, finding that app's selector,
 * and coming back. One list, rendered by the platform, works identically
 * everywhere and there is nothing for a new app to build.
 *
 * Switching keeps you **in the app you are already in**. Picking a tenant from
 * DMT lands you in DMT under the new tenant, not on someone's dashboard — which
 * is the entire point of doing this in the bar. The href comes from the same
 * per-app `href_template` the launcher uses, so no app changes its URL shape.
 *
 * You pick the tenant HERE. Being sent to a separate page to choose is the
 * behaviour this replaces, so the full-list link is for *managing* organizations,
 * not for switching to one.
 */
import { useEffect, useMemo, useRef, useState } from "react"
import { Icon } from "./icons"
import { orgInitials, orgLabel, type Org } from "./org"
import {
  filterTenants,
  findCurrent,
  tierLabel,
  type Tenant,
  type TenantsFailure,
} from "./tenants"

export interface TenantSwitcherProps {
  org: Org | null
  tenants: Tenant[]
  /** Go to this tenant. Returns false if the shell could not work out where. */
  onPick: (tenant: Tenant) => boolean
  /** Open the full organizations page — for managing them, not for switching. */
  onSeeAll: () => void
  /** Why the list is empty, when it is. Decides what the panel tells the user. */
  failure?: TenantsFailure
}

/**
 * One sentence per cause. The point is that a person reading it knows whether to
 * sign in, wait, or tell someone — rather than a single dead end that reads as
 * "broken" whatever went wrong.
 */
const FAILURE_MESSAGE: Record<TenantsFailure, string> = {
  unconfigured:
    "This app hasn’t been set up to list your organizations yet. Use the full list for now.",
  "no-token": "Sign in to see your organizations.",
  http: "Couldn’t load your organizations just now. Try the full list.",
  network: "Couldn’t reach the organization service from this app. Try the full list.",
  empty: "No organizations are available for your account.",
}

/** Deterministic tint per organization, so rows are distinguishable at a glance
 *  and a given org keeps the same colour every time it is listed. */
const TINTS = ["indigo", "green", "violet", "amber", "cyan", "rose", "teal", "sky"] as const
const tintFor = (id: string) => {
  let hash = 0
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0
  return TINTS[hash % TINTS.length]
}

export function TenantSwitcher({
  org,
  tenants,
  onPick,
  onSeeAll,
  failure,
}: TenantSwitcherProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [failed, setFailed] = useState<string | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  // Same shadow-root-aware dismissal the waffle uses: composedPath() is what
  // actually tells us whether a click was ours.
  useEffect(() => {
    if (!open) return
    const onDown = (e: Event) => {
      if (!e.composedPath().includes(panelRef.current as EventTarget)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("pointerdown", onDown, true)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("pointerdown", onDown, true)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      setQuery("")
      setFailed(null)
    } else {
      // Search is always present and always focused. An earlier version only
      // rendered it past a threshold, which meant the control you reach for was
      // sometimes simply absent.
      searchRef.current?.focus()
    }
  }, [open])

  const current = findCurrent(tenants, org)
  const shown = useMemo(() => filterTenants(tenants, query), [tenants, query])
  const hasList = tenants.length > 0

  /**
   * The real customer logo, derived from the organization's own website the same
   * way Optimize's sidebar already did it — so the platform shows ONE org mark
   * rather than a monogram up here and a logo down there.
   *
   * Host-supplied first, platform second — the same precedence as the rest of the
   * org handoff, and for the same reason: the host already knows. Waiting for the
   * platform costs a token acquisition AND a `/me/tenants` round trip before the
   * favicon request can even begin, which is why the logo used to appear a beat
   * or two after the page. A host that passes `org-info-url` gets it on first
   * paint. One that doesn't still gets it, just later.
   *
   * Falls back to the monogram whenever there is no site, neither source has
   * landed yet, or the image 404s — which is why this is an <img> with an onError
   * rather than a CSS background.
   */
  const infoUrl = org?.infoUrl || current?.infoUrl
  const logoUrl = infoUrl
    ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(infoUrl)}&sz=128`
    : null
  const [logoFailed, setLogoFailed] = useState(false)
  useEffect(() => setLogoFailed(false), [logoUrl])

  // One mechanism, deliberately: the element upgrades the stored org name from the
  // tenant list when the host gave only an id (see adoptPlatformOrgName), so
  // reading it here would ALSO override a label a host chose on purpose.
  const label = orgLabel()

  const pick = (tenant: Tenant) => {
    if (tenant.id === org?.id) {
      setOpen(false)
      return
    }
    // Don't close on failure — a menu that shuts having done nothing is the
    // worst outcome, and the reason belongs on screen rather than the console.
    if (onPick(tenant)) setOpen(false)
    else setFailed(`Couldn't open ${tenant.name} from here.`)
  }

  return (
    <div className="dex-tenant" ref={panelRef}>
      <button
        type="button"
        className="dex-tenant-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={org ? `Organization: ${label}. Switch organization` : "Select an organization"}
        onClick={() => setOpen((v) => !v)}
      >
        {logoUrl && !logoFailed ? (
          <img
            className="dex-org-logo"
            src={logoUrl}
            alt=""
            aria-hidden="true"
            onError={() => setLogoFailed(true)}
          />
        ) : (
          <span className="dex-org-mark" aria-hidden="true">
            {orgInitials()}
          </span>
        )}
        {/* Name over environment, two lines — the Auth0 tenant-picker shape.
            Inline, these were six elements in a single horizontal run (mark,
            name, badge, chevron, beside the app chip), which read as clutter and
            squeezed the name until real orgs truncated: "Hackensack Meri…".
            Stacking spends vertical space the 48px bar already has. */}
        <span className="dex-tenant-id">
          <span className="dex-tenant-name">{label}</span>
          {current && (
            // EVERY tier, production included. An earlier version hid PRODUCTION to
            // keep the bar quiet, which meant the common case showed no tag at all
            // and the feature read as missing. Knowing which environment you are in
            // is the whole point; colour carries the emphasis instead of presence.
            <span className={`dex-tier dex-tier-${current.tier}`}>{tierLabel(current.tier)}</span>
          )}
        </span>
        <span className="dex-chevron" aria-hidden="true">
          <Icon name="chevron" />
        </span>
      </button>

      {open && (
        <div className="dex-tenant-panel" role="menu">
          {hasList && (
            <input
              ref={searchRef}
              type="search"
              className="dex-tenant-search"
              placeholder="Search organizations"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search organizations"
            />
          )}

          {hasList ? (
            <div className="dex-tenant-list">
              {shown.map((t) => {
                const isCurrent = t.id === org?.id
                return (
                  <button
                    type="button"
                    key={t.id}
                    className="dex-tenant-row"
                    role="menuitem"
                    aria-current={isCurrent ? "true" : undefined}
                    onClick={() => pick(t)}
                  >
                    <span
                      className={`dex-tenant-icon dex-tint-${tintFor(t.id)}`}
                      aria-hidden="true"
                    >
                      <Icon name="building" />
                    </span>
                    <span className="dex-tenant-row-main">
                      <span className="dex-tenant-row-name">{t.name}</span>
                      <span className={`dex-tier dex-tier-${t.tier}`}>{tierLabel(t.tier)}</span>
                    </span>
                    {t.role && <span className="dex-tenant-role">{t.role}</span>}
                    {isCurrent && (
                      <span className="dex-tenant-check" aria-hidden="true">
                        <Icon name="check" />
                      </span>
                    )}
                  </button>
                )
              })}
              {!shown.length && <div className="dex-tenant-empty">No match for “{query}”.</div>}
            </div>
          ) : (
            <div className="dex-tenant-empty">
              {/* Still not the raw error — that belongs in the console — but which
                  KIND of failure it was, because "sign in" and "nobody configured
                  this" call for completely different actions from the reader. */}
              {failure ? FAILURE_MESSAGE[failure] : "Organizations couldn’t be loaded here."}
            </div>
          )}

          {failed && (
            <div className="dex-degraded" role="status">
              {failed}
            </div>
          )}

          <div className="dex-tenant-foot">
            <button
              type="button"
              className="dex-menu-row"
              onClick={() => {
                setOpen(false)
                onSeeAll()
              }}
            >
              <Icon name="building" />
              <span>Manage all tenants</span>
              <span className="dex-ext">
                <Icon name="external" />
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
